/**
 * List-B reader — the notes + email + system store (`projects/{vp}/messages`).
 *
 * P3 LENS MERGE (2026-07-09): the live WhatsApp thread comes from List A
 * (`wa_conversations`, read only through the `waListConversations` callable).
 * List B — this file — is the ONLY home of INTERNAL TEAM NOTES, EMAIL, and the
 * system/AI-draft docs the project thread showed before the rework. The merged
 * lens reads WhatsApp from List A and everything else from HERE, then merges by
 * timestamp.
 *
 * SYMMETRY (Vera gate, 2026-07-09): BOTH readers below filter with the SAME
 * shared `isListBMessage` predicate (lib/listBFilter.ts) — a DENY-list keyed on
 * the WhatsApp identity (`waMessageId` + channel spelling), NOT a
 * `channel in ['portal','email']` allow-list. So the person lens and the
 * project lens return the IDENTICAL non-WhatsApp set for the same message,
 * including legacy channel-less docs. Both do a client-side filter; ordering is
 * in-memory, so NO new index is required.
 *
 * Contact-level index note: the composite `collectionGroup('messages')`
 * index (contactId ASC · createdAt DESC) was deployed in M3.1 — this queries
 * DESC and reverses in-memory (chronological, oldest first).
 */
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Message } from './messageModel';
import { isListBMessage } from './listBFilter';

/**
 * PERSON lens (contact-level) — every note/email/system doc addressed to this
 * contact, across all their projects. WhatsApp (any wamid-bearing doc, either
 * channel spelling) and crossRef mirrors are dropped by the shared predicate,
 * so a message is never duplicated against the List-A live thread.
 */
export function subscribeContactNotesEmail(
  contactId: string,
  onUpdate: (messages: Message[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const q = query(
    collectionGroup(db, 'messages'),
    where('contactId', '==', contactId),
    orderBy('createdAt', 'desc'), // DESC matches the deployed composite index
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const msgs = snapshot.docs
        .filter((d) => isListBMessage(d.data() as Message))
        .map((d) => {
          // The doc's PARENT PATH is the true project home. Surface it so the
          // lens can tag General vs project messages.
          const parentDoc = d.ref.parent.parent;
          const parentPath = parentDoc ? parentDoc.path : undefined;
          const parentColl = parentDoc?.parent?.id;
          const parentProjectId =
            parentColl === 'projects' ? parentDoc?.id : undefined;
          return {
            id: d.id,
            ...d.data(),
            parentPath,
            parentProjectId,
          } as Message;
        })
        .reverse(); // chronological (oldest → newest)
      onUpdate(msgs);
    },
    (e) => onError?.(e),
  );
}

/**
 * PROJECT lens (project-level) — every note/email/system doc on THIS project.
 * Reads the whole `messages` subcollection and drops WhatsApp/crossRef with the
 * SAME shared predicate the contact reader uses (not a channel allow-list), so
 * legacy channel-less docs the pre-change ProjectThreadPanel rendered are KEPT.
 * Ordering is in-memory → no composite index.
 */
export function subscribeProjectNotesEmail(
  projectId: string,
  onUpdate: (messages: Message[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const q = query(collection(db, 'projects', projectId, 'messages'));
  return onSnapshot(
    q,
    (snapshot) => {
      const msgs = snapshot.docs
        .map((d) => ({ id: d.id, projectId, ...(d.data() as Omit<Message, 'id'>) }))
        .filter((m) => isListBMessage(m))
        .sort((a, b) => atMs(a) - atMs(b));
      onUpdate(msgs);
    },
    (e) => onError?.(e),
  );
}

/** Chronological sort key: server Timestamp when resolved, else the localTs fallback. */
function atMs(m: Message): number {
  const ts = m.createdAt as unknown as { toMillis?: () => number } | null;
  if (ts && typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof m.localTs === 'number') return m.localTs;
  return 0;
}
